import { Download, X } from "lucide-react";
import { useEffect, useState } from "react";

/** Minimal typing for the non-standard `beforeinstallprompt` event. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const DISMISSED_KEY = "catalogkit.pwa-install-dismissed";

/**
 * Custom "Install app" affordance driven by `beforeinstallprompt`. Dismissable
 * (persisted), and hidden permanently once the app is installed.
 */
export function InstallButton(): JSX.Element | null {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState<boolean>(
    () => localStorage.getItem(DISMISSED_KEY) === "1",
  );

  useEffect(() => {
    function onPrompt(event: Event): void {
      event.preventDefault();
      setDeferred(event as BeforeInstallPromptEvent);
    }
    function onInstalled(): void {
      setDeferred(null);
      localStorage.setItem(DISMISSED_KEY, "1");
    }
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (deferred === null || dismissed) return null;

  async function install(): Promise<void> {
    if (!deferred) return;
    await deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
  }

  function dismiss(): void {
    setDismissed(true);
    localStorage.setItem(DISMISSED_KEY, "1");
  }

  return (
    <div className="flex items-center gap-1 rounded-md border border-primary/30 bg-primary/5 px-2 py-1 text-xs">
      <button
        type="button"
        onClick={() => void install()}
        className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
      >
        <Download className="h-3.5 w-3.5" />
        Install app
      </button>
      <button type="button" onClick={dismiss} aria-label="Dismiss install prompt" className="text-muted-foreground hover:text-foreground">
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
